import { type ReactNode } from 'react'
import { cn } from '#app/utils/misc.js'

export function LegalContainer({
	children,
	className,
}: {
	className?: string
	children: ReactNode
}) {
	return (
		<div
			className={cn(
				'max-w-screen flex flex-col gap-lg text-body-sm md:gap-xxl md:text-body-md',
				className,
			)}
		>
			{children}
		</div>
	)
}

export function LegalSection({
	children,
	className,
}: {
	className?: string
	children: ReactNode
}) {
	return (
		<section
			className={cn(
				'container flex flex-col gap-sm md:gap-md xl:gap-lg',
				className,
			)}
		>
			{children}
		</section>
	)
}

export function LegalUnorderedList({
	children,
	className,
}: {
	className?: string
	children: ReactNode | ReactNode[]
}) {
	return (
		<ul
			className={cn(
				'ml-md flex list-inside list-disc flex-col gap-xxs md:gap-sm',
				className,
			)}
		>
			{children}
		</ul>
	)
}

export function LegalOrderedList({
	children,
	className,
}: {
	className?: string
	children: ReactNode | ReactNode[]
}) {
	return (
		<ol
			className={cn(
				'ml-md flex list-inside list-decimal flex-col gap-sm md:gap-md',
				className,
			)}
		>
			{children}
		</ol>
	)
}

export function LegalListItem({
	children,
	className,
}: {
	className?: string
	children: ReactNode
}) {
	return <li className={cn('list-item', className)}>{children}</li>
}

export function LegalParagraph({
	children,
	className,
}: {
	className?: string
	children: ReactNode
}) {
	return <p className={cn(className)}>{children}</p>
}

export function LegalHeading1({
	children,
	className,
}: {
	className?: string
	children: ReactNode
}) {
	return (
		<h1 className={cn('!font-serif text-h2 !font-light md:text-h1', className)}>
			{children}
		</h1>
	)
}

export function LegalHeading2({
	children,
	className,
}: {
	className?: string
	children: ReactNode
}) {
	return (
		<h2 className={cn('!font-serif text-h3 !font-light md:text-h2', className)}>
			{children}
		</h2>
	)
}

export function LegalSubtitle({
	children,
	className,
}: {
	className?: string
	children: ReactNode
}) {
	return <p className={cn('font-medium', className)}>{children}</p>
}

export function LegalHeading3({
	children,
	className,
}: {
	className?: string
	children: ReactNode
}) {
	return <h3 className={cn('text-h4 md:text-h3', className)}>{children}</h3>
}

export function LegalLink({
	children,
	className,
	href,
}: {
	className?: string
	children: ReactNode
	href: string
}) {
	return (
		<a
			className={cn('text-accent underline', className)}
			href={href}
			rel="noopener noreferrer"
			target="_blank"
		>
			{children}
		</a>
	)
}
